import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  FileSearch,
  RefreshCw,
  ShieldAlert,
  Users,
} from "lucide-react";

import {
  CandidateApplication,
  ProgramAccess,
  programApi,
} from "../api/program";
import { useAppDispatch } from "../hooks/reducer";
import { fetchUserData } from "../store/slices/auth";

const statusLabels: Record<string, string> = {
  new: "Нова заявка",
  contact_required: "Потрібно зв’язатися",
  interviewed: "Співбесіду пройдено",
  recommended: "Рекомендовано",
  approved: "Зараховано",
  reserve: "Резерв",
  rejected: "Відмова",
};

const steps = [
  { key: "new", label: "Заявку отримано", icon: FileSearch },
  { key: "review", label: "Розгляд адміністратором", icon: Clock3 },
  { key: "cohort", label: "Рішення та потік", icon: Users },
  { key: "approved", label: "Доступ до календаря", icon: CheckCircle2 },
];

const ApplicationStatusPage = () => {
  const dispatch = useAppDispatch();
  const [application, setApplication] =
    useState<CandidateApplication | null>(null);
  const [programAccess, setProgramAccess] =
    useState<ProgramAccess>("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadApplication = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await programApi.getMyApplication();
      setApplication(data.application);
      setProgramAccess(data.programAccess);
      if (data.programAccess === "active") {
        await dispatch(fetchUserData() as any);
      }
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message || "Не вдалося завантажити заявку."
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  const activeStep = useMemo(() => {
    if (programAccess === "active" || application?.status === "approved") return 4;
    if (["recommended", "reserve", "rejected"].includes(application?.status || ""))
      return 3;
    if (
      ["contact_required", "interviewed"].includes(application?.status || "")
    )
      return 2;
    return 1;
  }, [application?.status, programAccess]);

  if (loading) {
    return <div className="p-8 text-[#6F6962]">Завантажуємо заявку...</div>;
  }

  return (
    <section className="px-3 py-5 sm:px-6 sm:py-7 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-xl border border-[#E3DED6] bg-white p-4 sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                Кандидат{" "}
                <span className="notranslate" translate="no">
                  МОЖУ
                </span>
              </p>
              <h2 className="mt-1 text-2xl font-bold text-[#29251F] sm:text-3xl">
                Статус вашої заявки
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#777068]">
                Тут з’являтимуться всі зміни. Після зарахування система
                автоматично відкриє календар обраного потоку.
              </p>
            </div>
            <button
              type="button"
              onClick={loadApplication}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-[#DDD7CF] px-4 text-sm font-semibold text-[#625C55] hover:bg-[#F7F4EF] sm:w-auto"
            >
              <RefreshCw size={16} />
              Оновити
            </button>
          </div>

          {error && (
            <div className="mt-6 flex items-center gap-3 rounded-lg border border-[#E9BDB5] bg-[#FFF3F1] p-4 text-sm text-[#9D3D2E]">
              <ShieldAlert size={18} />
              {error}
            </div>
          )}

          <div className="mt-8 grid gap-3 md:grid-cols-4">
            {steps.map(({ key, label, icon: Icon }, index) => {
              const completed = index + 1 <= activeStep;
              return (
                <div
                  key={key}
                  className={`rounded-lg border p-4 ${
                    completed
                      ? "border-[#F3C6B4] bg-[#FFF3ED]"
                      : "border-[#E4E0D9] bg-[#FAF9F7]"
                  }`}
                >
                  <div
                    className={`grid h-9 w-9 place-items-center rounded-lg ${
                      completed
                        ? "border-2 border-black bg-[#ffd400] text-black"
                        : "bg-[#EDE9E3] text-[#8B847B]"
                    }`}
                  >
                    <Icon size={18} />
                  </div>
                  <p className="mt-3 text-sm font-semibold text-[#403B35]">
                    {label}
                  </p>
                </div>
              );
            })}
          </div>

          {application && (
            <div className="mt-7 grid gap-5 lg:grid-cols-[1fr_280px]">
              <div className="rounded-lg border border-[#E4E0D9] p-5">
                <p className="text-xs font-semibold uppercase text-[#918A82]">
                  Поточний статус
                </p>
                <p className="mt-2 text-xl font-bold text-[#312D28]">
                  {statusLabels[application.status] || application.status}
                </p>
                {application.adminComment && (
                  <div className="mt-4 rounded-lg bg-[#F7F4EF] p-4">
                    <p className="text-xs font-semibold text-[#8A837A]">
                      Коментар адміністратора
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#514C46]">
                      {application.adminComment}
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-[#D9E2EA] bg-[#F4F8FB] p-5">
                <p className="text-sm font-semibold text-[#52616D]">
                  Заявку подано
                </p>
                <p className="mt-2 text-lg font-bold text-[#2D353C]">
                  {new Date(application.createdAt).toLocaleDateString("uk-UA")}
                </p>
                <p className="mt-4 text-xs leading-5 text-[#68747D]">
                  Не потрібно подавати анкету повторно. Просто оновлюйте цю
                  сторінку.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ApplicationStatusPage;
